import React, { useEffect, useState } from "react";
import { fetchZAttributes, upSertZAttribute } from "../../../api";
import Select, { ActionMeta, MultiValue, SingleValue } from "react-select";
import CustomModal from "../../widgets/components/CustomModal";
import { CirclePicker } from "react-color";
import { AttrType, ZAttributes } from "../../../types";
import { mergeSelectStyles } from "../../widgets/components/SelectStyles";
// import './CategoryPicker.css';



export interface Category {
  categoryId: string;
  categoryName: string;
  categoryColor: string;
}

interface OptionType {
  value: string;
  label: string;
  color?: string;
}

interface CategoryPickerProps {
  categoryType: AttrType;
  tabIndex?: number;
  onCategoryChange: (categoryId: string) => void;
  selectedCategory?: string;
}

export function CategoryPicker({
  categoryType,
  tabIndex,
  onCategoryChange,
  selectedCategory,
}: CategoryPickerProps) {
  const [categories, setCategories] = useState<OptionType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const fetchedCategories: ZAttributes[] = await fetchZAttributes(categoryType);
        const categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories : [fetchedCategories];
        const options = categoriesArray.map(category => ({
          value: category.attrId,
          label: category.attrNm,
          color: category.attrColor,
        }));
        setCategories(options);

        // Set the default selected category if provided
        if (selectedCategory) {
          const defaultOption = options.find(option => option.value === selectedCategory) || null;
          setSelectedOption(defaultOption);
        }
      } catch (error) {
        console.error(`Error fetching ${categoryType}:`, error);
      }
    };

    getCategories();
  }, [categoryType, selectedCategory]);

  const customStyles = mergeSelectStyles({
    option: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    singleValue: (provided: any, { data }: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
  });

  const formatOptionLabel = ({ label, color }: OptionType) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {color && (
        <span style={{ backgroundColor: color, width: 16, height: 16, display: 'inline-block', marginRight: 8 }}></span>
      )}
      {label}
    </div>
  );

  const handleSelectChange = (selectedOption: SingleValue<OptionType> | MultiValue<OptionType>, _actionMeta: ActionMeta<OptionType>) => {
    const option = selectedOption as SingleValue<OptionType>;
    if (option && option.value === 'add_new') {
      setIsModalOpen(true);
    } else {
      setSelectedOption(option);
      onCategoryChange(option?.value || '');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddNew = (newOption: OptionType) => {
    setIsModalOpen(false);
    setCategories(prevCategories => [...prevCategories, newOption]);
    setSelectedOption(newOption);
    onCategoryChange(newOption.value);
  };

  const modifiedOptions = [
    { value: 'add_new', label: 'Add New'.toTranslatedDisplay(), color: '' },
    ...categories,
  ];

  return (
    <div className="searchable-picker">
      <Select
        className="react-select-styled react-select"
        classNamePrefix="react-select"
        options={modifiedOptions}
        formatOptionLabel={formatOptionLabel}
        styles={customStyles}
        placeholder={`Select ${categoryType}`}
        value={selectedOption}
        onChange={handleSelectChange}
        tabIndex={tabIndex}
        isClearable
      />
      <CustomModal
        title={`${'Add New'.toTranslatedDisplay()} ${(categoryType || '').toTranslatedDisplay()}`}
        isVisible={isModalOpen}
        onClose={handleModalClose}
        body={<CategoryForm categoryType={categoryType} onAddNew={handleAddNew} />}
        id={''}
        onSave={() => {}}
      />
    </div>
  );
}

interface CategoryFormProps {
  categoryType: AttrType;
  onAddNew: (newOption: OptionType) => void;
}

export function CategoryForm({ categoryType, onAddNew }: CategoryFormProps) {
  const [category, setCategory] = useState<ZAttributes>(new ZAttributes({ attrType: categoryType }));
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCategory(prevCategory => ({
      ...prevCategory,
      [name]: value
    }));
  };

  const handleColorChange = (color: any) => {
    setCategory(prevCategory => ({
      ...prevCategory,
      attrColor: color.hex
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const updatedCategory = await upSertZAttribute(category);
      const newOption = {
        value: updatedCategory.attrId,
        label: updatedCategory.attrNm || '',
        color: updatedCategory.attrColor
      };
      onAddNew(newOption);
      setCategory(new ZAttributes({attrType:categoryType}))
    } catch (error) {
      console.error(`Error upserting ${categoryType}:`, error);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">{'Category Name'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className="form-control"
            placeholder={`${categoryType} Name`}
            name="attrNm"
            value={category.attrNm}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{'Category Description'.toTranslatedDisplay()}</label>
          <input
            type="text"
            className="form-control"
            placeholder={`${categoryType} Description`}
            name="attrTxt"
            value={category.attrTxt}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{'Category Color'.toTranslatedDisplay()}</label>
          <CirclePicker
            color={category.attrColor}
            onChangeComplete={handleColorChange}
          />
        </div>

        <button type="submit" className="btn btn-primary">{'Save'.toTranslatedDisplay()}</button>
      </form>
    </div>
  );
}
